<template>
  <div class="reporting-section">
    <div class="section-header">
      <i class="material-icons section-icon">library_books</i>
      <h2>{{ $t('dash.reporting.section.posts.title') }}</h2>
    </div>

    <div class="post-empty" v-if="!sortedPosts.length">
      <i class="material-icons">inbox</i>
      <p>{{ $t('dash.reporting.no_posts') }}</p>
    </div>

    <template v-else>
      <div class="post-table">
        <div class="post-table__head" :style="{ gridTemplateColumns: gridTemplate }">
          <span class="col-header sortable" :class="sortClass('date')" @click="onSort('date')">
            {{ cap($t('dash.reporting.pdf.listItem.date')) }}<i class="material-icons sort-icon">{{ sortIcon('date') }}</i>
          </span>
          <span class="col-header">{{ cap($t('dash.reporting.pdf.listItem.publication')) }}</span>
          <span class="col-header sortable align-right" :class="sortClass('impressions')" @click="onSort('impressions')">
            {{ cap($t('dash.reporting.pdf.listItem.print')) }}<i class="material-icons sort-icon">{{ sortIcon('impressions') }}</i>
          </span>
          <span class="col-header sortable align-right" :class="sortClass('reactions')" @click="onSort('reactions')">
            {{ cap($t('dash.reporting.pdf.listItem.reactions')) }}<i class="material-icons sort-icon">{{ sortIcon('reactions') }}</i>
          </span>
          <span class="col-header sortable align-right" :class="sortClass('comments')" @click="onSort('comments')">
            {{ cap($t('dash.reporting.pdf.listItem.comments')) }}<i class="material-icons sort-icon">{{ sortIcon('comments') }}</i>
          </span>
          <span v-if="showShares" class="col-header sortable align-right" :class="sortClass('shares')" @click="onSort('shares')">
            {{ cap($t('dash.reporting.pdf.listItem.shares')) }}<i class="material-icons sort-icon">{{ sortIcon('shares') }}</i>
          </span>
          <span v-if="showClicks" class="col-header sortable align-right" :class="sortClass('clicks')" @click="onSort('clicks')">
            {{ cap($t('dash.reporting.clic')) }}<i class="material-icons sort-icon">{{ sortIcon('clicks') }}</i>
          </span>
          <span class="col-header sortable align-right" :class="sortClass('engagement')" @click="onSort('engagement')">
            {{ cap($t('dash.reporting.pdf.listItem.engagement')) }}<i class="material-icons sort-icon">{{ sortIcon('engagement') }}</i>
          </span>
          <span class="col-header sortable align-right score-header" :class="sortClass('score')" @click="onSort('score')">
            Score<span class="score-info" :title="scoreFormula">ⓘ</span><i class="material-icons sort-icon">{{ sortIcon('score') }}</i>
          </span>
        </div>
        <div class="post-table__row" v-for="post in paginatedPosts" :key="post.id || post.post_id" :style="{ gridTemplateColumns: gridTemplate }">
          <span class="cell-date">{{ formatDate(post.created_time) }}</span>
          <div class="cell-post">
            <div class="post-thumb">
              <img v-if="post.attachment_url" :src="post.attachment_url" alt="">
              <i v-else class="material-icons">image</i>
            </div>
            <div class="post-text">
              <p class="post-msg">{{ post.message }}</p>
              <span class="type-badge" :class="typeBadgeClass(post.type)">{{ typeLabel(post.type) }}</span>
            </div>
          </div>
          <span class="cell-num">{{ fmtImpression(post) }}</span>
          <span class="cell-num">{{ fmtReactions(post) }}</span>
          <span class="cell-num">{{ post.comments_count || 0 }}</span>
          <span v-if="showShares" class="cell-num">{{ post.shares_count || 0 }}</span>
          <span v-if="showClicks" class="cell-num">{{ post.link_clicks || post.click_count || 0 }}</span>
          <span class="cell-num">{{ post.reach_engagement ? post.reach_engagement.toFixed(2) + '%' : '—' }}</span>
          <span class="cell-stars" :title="scoreFormula">{{ starsFor(post.reach_engagement) }}</span>
        </div>
      </div>

      <div class="pagination" v-if="pageCount > 1">
        <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
          <i class="material-icons">chevron_left</i>
        </button>
        <span class="page-info">{{ currentPage }} / {{ pageCount }}</span>
        <button class="page-btn" :disabled="currentPage === pageCount" @click="currentPage++">
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
const PAGE_SIZE = 20

export default {
  name: 'ReportingContent',
  props: {
    stats: { type: Object, required: true },
    page: { type: Object, default: null },
  },
  data() {
    return {
      sortKey: 'date',
      sortDir: 'desc',
      currentPage: 1,
    }
  },
  computed: {
    network() {
      return this.page ? (this.page.network || '') : ''
    },
    showShares() {
      return true
    },
    showClicks() {
      return this.network === 'facebook' || this.network === 'linkedin'
    },
    gridTemplate() {
      var cols = ['90px', '1fr', '110px', '95px', '115px']
      if (this.showShares) cols.push('85px')
      if (this.showClicks) cols.push('65px')
      cols.push('100px', '70px')
      return cols.join(' ')
    },
    scoreFormula() {
      return this.$t('dash.reporting.score_formula')
    },
    posts() {
      return this.stats.posts || (this.stats.page && this.stats.page.posts) || []
    },
    sortedPosts() {
      var list = this.posts.slice()
      var dir = this.sortDir === 'asc' ? 1 : -1
      var key = this.sortKey
      list.sort(function(a, b) {
        var va, vb
        if (key === 'date')        { va = new Date(a.created_time);  vb = new Date(b.created_time) }
        else if (key === 'impressions') { va = a.post_impressions || a.view_count || 0; vb = b.post_impressions || b.view_count || 0 }
        else if (key === 'reactions')   { va = a.post_reactions_sum || a.like_count || 0; vb = b.post_reactions_sum || b.like_count || 0 }
        else if (key === 'comments')    { va = a.comments_count || 0; vb = b.comments_count || 0 }
        else if (key === 'shares')      { va = a.shares_count || 0; vb = b.shares_count || 0 }
        else if (key === 'clicks')      { va = a.link_clicks || a.click_count || 0; vb = b.link_clicks || b.click_count || 0 }
        else if (key === 'engagement' || key === 'score') { va = a.reach_engagement || 0; vb = b.reach_engagement || 0 }
        else                            { va = new Date(a.created_time); vb = new Date(b.created_time) }
        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
      return list
    },
    pageCount() {
      return Math.ceil(this.sortedPosts.length / PAGE_SIZE)
    },
    paginatedPosts() {
      var start = (this.currentPage - 1) * PAGE_SIZE
      return this.sortedPosts.slice(start, start + PAGE_SIZE)
    },
  },
  watch: {
    paginatedPosts: {
      immediate: true,
      handler(posts) {
        if (posts && posts.length) this._fetchPageImages(posts)
      },
    },
  },
  methods: {
    async _fetchPageImages(posts) {
      if (!this.page) return
      var net = this.page.network || ''
      if (!['facebook', 'instagram', 'tiktok'].includes(net)) return
      var { VUE_APP_API_HOST } = process.env
      var token = localStorage.getItem('token')
      var vm = this
      var BATCH = 5
      var needFetch = posts.filter(function(post) {
        return !(post.attachment_url || post.full_picture || post.thumbnail_url || post.media_url || post.cover_image_url)
      })
      for (var i = 0; i < needFetch.length; i += BATCH) {
        await Promise.all(needFetch.slice(i, i + BATCH).map(async function(post) {
          var postId = post.post_id || post.id
          if (!postId) return
          try {
            if (net === 'tiktok') {
              if (post.cover_image_url) vm.$set(post, 'attachment_url', post.cover_image_url)
              return
            }
            var res = await fetch(VUE_APP_API_HOST + '/moderation/post?page=' + vm.page.id + '&post=' + postId + '&inbox=true', {
              headers: { Authorization: token ? 'Bearer ' + token : '' },
            })
            if (!res.ok) return
            var data = await res.json()
            if (!data || !data.post) return
            var imgUrl = net === 'facebook'
              ? data.post.full_picture
              : (data.post.thumbnail_url || data.post.media_url)
            if (imgUrl) vm.$set(post, 'attachment_url', imgUrl)
          } catch (e) { /* ignore */ }
        }))
      }
    },
    onSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortDir = 'desc'
      }
      this.currentPage = 1
    },
    sortClass(key) {
      return { 'col-active': this.sortKey === key }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return 'unfold_more'
      return this.sortDir === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },
    fmtImpression(post) {
      if (this.network === 'tiktok') return post.post_impressions || post.view_count || 0
      return post.post_impressions || 0
    },
    fmtReactions(post) {
      if (this.network === 'tiktok') return post.like_count || 0
      return post.post_reactions_sum || post.like_count || 0
    },
    formatDate(dateString) {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    starsFor(pct) {
      if (pct == null) return '—'
      var stars = 0
      if (pct >= 7) stars = 5
      else if (pct >= 4) stars = 4
      else if (pct >= 2) stars = 3
      else if (pct >= 1) stars = 2
      else if (pct >= 0.5) stars = 1
      return '★'.repeat(stars) + '☆'.repeat(5 - stars)
    },
    typeLabel(type) {
      var map = {
        photo: 'Image', image: 'Image', IMAGE: 'Image',
        video: 'Vidéo', VIDEO: 'Vidéo',
        snippet: 'Lien', link: 'Lien',
        text: 'Texte', text_only: 'Texte', status: 'Texte',
        Story: 'Story', story: 'Story',
        carrousel: 'Carrousel', CAROUSEL_ALBUM: 'Carrousel', 'MULTI-IMAGE': 'Carrousel',
      }
      return map[type] || type || '—'
    },
    cap(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    typeBadgeClass(type) {
      if (!type) return 'badge-default'
      var t = type.toLowerCase()
      if (t === 'photo' || t === 'image') return 'badge-image'
      if (t === 'video') return 'badge-video'
      if (t === 'snippet' || t === 'link') return 'badge-link'
      if (t === 'story') return 'badge-story'
      if (t === 'carrousel' || t === 'multi-image' || t === 'carousel_album') return 'badge-carousel'
      return 'badge-default'
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }

.post-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  color: #9ba3b7;
}
.post-empty .material-icons { font-size: 40px; color: #e0d0ea; }
.post-empty p { font-size: 14px; }

.post-table { width: 100%; overflow-x: auto; }
.post-table__head,
.post-table__row {
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 8px 4px;
}
.post-table__head {
  border-bottom: 1px solid #f1edf5;
  padding-bottom: 10px;
}
.post-table__row {
  border-bottom: 1px solid #f9f7fb;
  font-size: 13px;
  color: #121826;
}
.post-table__row:last-child { border-bottom: none; }
.align-right { text-align: right; justify-content: flex-end; }

.col-header {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
}
.col-header.sortable {
  cursor: pointer;
  user-select: none;
}
.col-header.sortable:hover { color: #6B1F4D; }
.col-header.col-active { color: #6B1F4D; }
.sort-icon { font-size: 14px; opacity: 0.6; }
.col-active .sort-icon { opacity: 1; }

.cell-date { color: #9ba3b7; font-size: 12px; white-space: nowrap; min-width: 0; overflow: hidden; }
.cell-post { display: flex; align-items: flex-start; gap: 10px; min-width: 0; }
.post-text { display: flex; flex-direction: column; gap: 4px; min-width: 0; align-items: flex-start; }
.post-thumb {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 5.6px;
  overflow: hidden;
  background: #f1edf5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0a8d0;
}
.post-thumb img { width: 100%; height: 100%; object-fit: cover; }
.post-thumb .material-icons { font-size: 18px; }
.post-msg {
  font-size: 12px;
  color: #555E75;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0;
}
.cell-num { text-align: right; font-size: 13px; font-weight: 500; min-width: 0; }
.cell-stars {
  text-align: right;
  font-size: 12px;
  color: #f59e0b;
  letter-spacing: 1px;
  cursor: help;
}
.score-header { gap: 4px; }
.score-info { font-size: 11px; color: #9ba3b7; cursor: help; }

.type-badge {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
  max-width: fit-content;
}
.badge-image { background: #dcfce7; color: #166534; }
.badge-video { background: #dbeafe; color: #1e40af; }
.badge-link { background: #ffedd5; color: #9a3412; }
.badge-story { background: #f3e8ff; color: #6b21a8; }
.badge-carousel { background: #cffafe; color: #164e63; }
.badge-default { background: #f1f5f9; color: #475569; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f1edf5;
}
.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e0d0ea;
  background: #fff;
  color: #6B1F4D;
  cursor: pointer;
  transition: all 0.15s;
}
.page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.page-btn:not(:disabled):hover { background: #f1edf5; }
.page-btn .material-icons { font-size: 18px; }
.page-info { font-size: 13px; color: #555E75; font-weight: 500; min-width: 60px; text-align: center; }
</style>
